"use client"
import * as ModalPrimitive from "@radix-ui/react-dialog"
import React, {
  ComponentProps,
  ElementRef,
  forwardRef,
  useCallback,
  useState,
} from "react"
import { Close } from "../../icons/material-symbols/outlined/Close"
import { overlayVariants } from "../../styles/utils/overlay"
import { classNames } from "../../utils"
import { Button } from "../Button"
import { VisuallyHidden } from "../VisuallyHidden"

export type LightboxProps = Pick<
  ComponentProps<typeof ModalPrimitive.Root>,
  "open" | "defaultOpen" | "onOpenChange" | "modal"
> &
  Omit<ComponentProps<typeof ModalPrimitive.Content>, "content"> & {
    content: React.ReactNode
    disabled?: boolean
    overrides?: {
      Overlay: ComponentProps<typeof ModalPrimitive.Overlay>
    }
  }

export const Lightbox = forwardRef<
  ElementRef<typeof ModalPrimitive.Content>,
  LightboxProps
>(function Lightbox(
  {
    open,
    defaultOpen,
    onOpenChange,
    content,
    children,
    className,
    modal,
    overrides,
    disabled,
    ...props
  },
  ref,
) {
  // We keep track of opened state internally so we can close on "fake" overlay clicks
  const [openedState, setOpenedState] = useState(defaultOpen ?? open)
  const resolvedOpenState = open === undefined ? openedState : open
  const handleOnOpenChange = useCallback(
    (open: boolean) => {
      setOpenedState(open)
      onOpenChange?.(open)
    },
    [onOpenChange],
  )

  if (disabled) {
    return children
  }

  return (
    <>
      <ModalPrimitive.Root
        defaultOpen={defaultOpen}
        modal={modal}
        open={resolvedOpenState}
        onOpenChange={handleOnOpenChange}
      >
        <ModalPrimitive.Trigger asChild className="cursor-pointer">
          {children}
        </ModalPrimitive.Trigger>
        <ModalPrimitive.Portal>
          <ModalPrimitive.Overlay
            data-testid="Overlay"
            {...overrides?.Overlay}
            className={classNames(
              overlayVariants(),
              "z-modal flex items-center justify-center",
              overrides?.Overlay.className,
            )}
          >
            <ModalPrimitive.Content
              aria-describedby={undefined}
              className={classNames(
                "z-modal flex items-center justify-center rounded-xl ease-out-quint",
                "size-[min(calc(100vh-50px),calc(100vw-50px))]",
                "data-[state=open]:animate-in data-[state=closed]:animate-out",
                "data-[state=closed]:duration-300 data-[state=open]:duration-300",
                "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
                "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
                "focus-visible:outline-hidden",
                className,
              )}
              ref={ref}
              onClick={e => {
                if (e.target === e.currentTarget) {
                  handleOnOpenChange(false)
                }
              }}
              onOpenAutoFocus={event => event.preventDefault()}
              {...props}
            >
              <VisuallyHidden>
                <ModalPrimitive.Title />
              </VisuallyHidden>
              {content}
            </ModalPrimitive.Content>
            <ModalPrimitive.Close
              asChild
              className="absolute right-6 top-6 z-modal"
            >
              <Button icon={Close} rounded variant="secondary" />
            </ModalPrimitive.Close>
          </ModalPrimitive.Overlay>
        </ModalPrimitive.Portal>
      </ModalPrimitive.Root>
    </>
  )
})
